import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, XCircle, ClockClockwise } from "@phosphor-icons/react";

const STEP_LABEL = {
  2: "RM",
  3: "FM",
  4: "COO",
  5: "CEO",
};

const STEP_FULL = {
  2: "Regional Manager",
  3: "Finance / Functional Manager",
  4: "Chief Operating Officer",
  5: "Chief Executive Officer",
};

export default function Approvals() {
  const { user } = useAuth();
  const tier = user?.ux_tier ?? 0;
  const [tab, setTab] = useState("for_me");      // for_me | mine | all
  const [statusFilter, setStatusFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { status: statusFilter === "all" ? "all" : statusFilter };
      if (tab === "for_me") params.for_me = true;
      if (tab === "mine") params.mine = true;
      const { data } = await api.get("/deletion/requests", { params });
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed to load."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, statusFilter]);

  const decide = async (req, action) => {
    const comment = action === "reject"
      ? window.prompt("Reason for rejecting this deletion request:") || ""
      : window.prompt("Optional comment for the audit log (press OK to skip):") || "";
    if (action === "reject" && comment.trim().length === 0) return;
    setBusyId(req.request_id);
    try {
      await api.post(`/deletion/requests/${req.request_id}/${action}`, { comment });
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Failed.");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Deletion Workflow</div>
          <h1 className="font-display font-bold text-[28px]">Approvals</h1>
          <p className="text-[12px] text-[var(--span-text-2)] mt-1 max-w-[640px]">
            Deletion requests follow a 4-stage chain — RM → FM → COO → CEO. Every step
            is audit-logged. Approved records sit in the <strong>Recycle Bin</strong> for
            two years before automatic purge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {["for_me", "mine", "all"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`span-btn h-8 text-[11px] whitespace-nowrap ${tab === t ? "" : "span-btn-ghost"}`}
            >
              {t === "for_me" ? `For me (T${tier})` : t === "mine" ? "Requested by me" : "All"}
            </button>
          ))}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter"
            className="span-input h-8 text-[11px] min-w-[110px]"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {err && <div className="card-flat p-4 text-[12px] text-[var(--span-danger)]">{err}</div>}

      <div className="card-flat overflow-hidden">
        <table className="span-table" data-testid="approvals-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Record</th>
              <th>Requested by</th>
              <th>Reason</th>
              <th>Stage</th>
              <th>Status</th>
              <th>When</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-8 text-[var(--span-text-2)] font-mono text-[11px]">LOADING…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--span-text-2)] text-[12px]">No deletion requests match this view.</td></tr>
            )}
            {items.map(req => {
              const canActOnThis = req.status === "pending" && tier >= (req.current_level ?? 99) && tab !== "mine";
              return (
                <tr key={req.request_id} data-testid={`req-row-${req.request_id}`}>
                  <td><span className="badge">{req.entity_type.replace("_", " ")}</span></td>
                  <td className="font-mono text-[12px]">{req.entity_label || req.entity_id}</td>
                  <td className="text-[12px]">{req.requested_by_name || "—"}</td>
                  <td className="max-w-[280px] truncate text-[12px]" title={req.reason}>{req.reason}</td>
                  <td>
                    <span className="badge badge-warn font-mono">
                      {STEP_LABEL[req.current_level] || "—"} (lvl {req.current_level})
                    </span>
                    <div className="text-[10px] text-[var(--span-text-2)] mt-0.5 font-mono uppercase tracking-[0.1em]">
                      {req.approvals?.length || 0}/4 steps
                    </div>
                  </td>
                  <td>
                    {req.status === "pending" && <span className="badge badge-warn">pending</span>}
                    {req.status === "approved" && <span className="badge badge-success">approved</span>}
                    {req.status === "rejected" && <span className="badge badge-danger">rejected</span>}
                    {req.force_delete && <div className="text-[9px] text-[var(--span-danger)] mt-0.5 font-mono uppercase">FORCE</div>}
                  </td>
                  <td className="text-[11px] text-[var(--span-text-2)] font-mono">{fmtDate(req.requested_at)}</td>
                  <td className="text-right">
                    {canActOnThis ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          disabled={busyId === req.request_id}
                          onClick={() => decide(req, "approve")}
                          data-testid={`approve-${req.request_id}`}
                          className="span-btn h-7 text-[10px] disabled:opacity-50"
                          style={{ background: "var(--span-success, #10B981)", borderColor: "var(--span-success, #10B981)" }}
                        >
                          <CheckCircle size={11} /> Approve
                        </button>
                        <button
                          disabled={busyId === req.request_id}
                          onClick={() => decide(req, "reject")}
                          data-testid={`reject-${req.request_id}`}
                          className="span-btn span-btn-ghost h-7 text-[10px] disabled:opacity-50"
                          style={{ color: "var(--span-danger)" }}
                        >
                          <XCircle size={11} /> Reject
                        </button>
                      </div>
                    ) : req.status === "pending" ? (
                      <span className="text-[10px] font-mono text-[var(--span-text-2)] inline-flex items-center gap-1 uppercase tracking-[0.1em]">
                        <ClockClockwise size={11} /> Waiting on {STEP_FULL[req.current_level]}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono text-[var(--span-text-2)] uppercase tracking-[0.1em]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
